import React, { useState, useEffect } from 'react';
import './App.css';
import AuthForm from './AuthForm';
import UserMenu from './UserMenu';

// ─── Config ────────────────────────────────────────────────────────────────────
const ACTIVITIES = [
  { id: 'bible',   label: 'Lire la Bible',           icon: '📖', bonus: 15, desc: 'Méditer la Parole de Dieu'    },
  { id: 'mission', label: 'Effort missionnaire',      icon: '🌍', bonus: 20, desc: 'Rejoindre une mission locale' },
  { id: 'gospel',  label: "Partager l'Évangile",      icon: '🗣️', bonus: 20, desc: 'Témoigner de la foi'          },
  { id: 'prayer',  label: 'Réunion de prière',        icon: '🙏', bonus: 15, desc: 'Prier en communauté'          },
  { id: 'sabbath', label: "Sabbat à l'Église",        icon: '⛪', bonus: 25, desc: 'Observer le saint repos'      },
  { id: 'sermon',  label: 'Écouter une prédication',  icon: '🎤', bonus: 10, desc: 'Se nourrir de la Parole'      },
  { id: 'food',    label: 'Donner de la nourriture',  icon: '🍞', bonus: 10, desc: 'Servir les nécessiteux'       },
  { id: 'help',    label: 'Aider son prochain',       icon: '🤝', bonus: 10, desc: 'Secourir une personne'       },
];

const DEFAULT_STATE = {
  avatarName: 'Mon Avatar',
  level: 75,
  decayRate: 5,
  decayPeriod: 'hourly',
  lastUpdated: new Date().toISOString(),
  log: [],
  todayActivities: [],
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
function getLevelInfo(level) {
  if (level >= 80) return { label: "Rempli de l'Esprit ✨", color: '#00ff88' };
  if (level >= 60) return { label: "Marchant dans l'Esprit",  color: '#88ff00' };
  if (level >= 40) return { label: 'En quête spirituelle',    color: '#ffd700' };
  if (level >= 20) return { label: 'Besoin de renouveau',     color: '#ff8800' };
  return            { label: 'Danger spirituel !',            color: '#ff3333' };
}

function plumbobColor(level) {
  if (level >= 80) return '#00ff88';
  if (level >= 60) return '#aeff00';
  if (level >= 40) return '#ffd700';
  if (level >= 20) return '#ff8800';
  return '#ff3333';
}

function toDisplayDate(d) {
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}

// ─── Avatar SVG ────────────────────────────────────────────────────────────────
function AvatarSVG() {
  return (
    <svg className="avatar-svg" viewBox="0 0 100 170" xmlns="http://www.w3.org/2000/svg">
      {/* Halo */}
      <ellipse cx="50" cy="10" rx="26" ry="7" fill="none" stroke="#FFD700" strokeWidth="3"
        style={{ filter: 'drop-shadow(0 0 6px #FFD700)' }} />
      {/* Head */}
      <circle cx="50" cy="38" r="22" fill="#f5c18a" />
      <circle cx="42" cy="34" r="3.5" fill="#3d2b1f" />
      <circle cx="58" cy="34" r="3.5" fill="#3d2b1f" />
      <circle cx="43.5" cy="32.5" r="1" fill="white" />
      <circle cx="59.5" cy="32.5" r="1" fill="white" />
      <path d="M 42 46 Q 50 54 58 46" fill="none" stroke="#3d2b1f" strokeWidth="2" strokeLinecap="round" />
      {/* Body / Robe */}
      <path d="M 26 64 Q 20 115 22 158 L 78 158 Q 80 115 74 64 Q 63 55 50 58 Q 37 55 26 64Z" fill="#2a5fcc" />
      {/* Robe sheen */}
      <path d="M 38 65 Q 34 100 35 140" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="5" strokeLinecap="round" />
      {/* Cross */}
      <line x1="50" y1="82" x2="50" y2="128" stroke="#FFD700" strokeWidth="5" strokeLinecap="round"
        style={{ filter: 'drop-shadow(0 0 4px #FFD700)' }} />
      <line x1="33" y1="100" x2="67" y2="100" stroke="#FFD700" strokeWidth="5" strokeLinecap="round"
        style={{ filter: 'drop-shadow(0 0 4px #FFD700)' }} />
      {/* Hands */}
      <circle cx="18" cy="108" r="9" fill="#f5c18a" />
      <circle cx="82" cy="108" r="9" fill="#f5c18a" />
    </svg>
  );
}

// ─── Settings Modal ─────────────────────────────────────────────────────────────
function SettingsModal({ avatarName, decayRate, decayPeriod, onSave, onReset, onClose }) {
  const [name, setName]     = useState(avatarName);
  const [decay, setDecay]   = useState(decayRate);
  const [period, setPeriod] = useState(decayPeriod || 'hourly');
  const [confirmReset, setConfirmReset] = useState(false);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2>⚙️ Paramètres</h2>

        <div className="modal-field">
          <label>Nom de l'avatar</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={30}
            placeholder="Ex. : Jean-Paul"
          />
        </div>

        <div className="modal-field">
          <label>Déclin — <strong>–{decay}% par période</strong></label>
          <input
            type="range" min="1" max="20" step="1"
            value={decay}
            onChange={e => setDecay(+e.target.value)}
          />
          <div className="range-labels"><span>1%</span><span>10%</span><span>20%</span></div>
        </div>

        <div className="modal-field">
          <label>Période de déclin</label>
          <select value={period} onChange={e => setPeriod(e.target.value)}>
            <option value="minutely">Chaque minute</option>
            <option value="hourly">Chaque heure</option>
            <option value="daily">Chaque jour</option>
          </select>
        </div>

        <div className="modal-actions">
          <button className="btn-primary" onClick={() => onSave(name, decay, period)}>Sauvegarder</button>
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
        </div>

        <hr />
        {!confirmReset
          ? <button className="btn-danger" onClick={() => setConfirmReset(true)}>Réinitialiser le jeu</button>
          : <div className="confirm-reset">
              <p>Êtes-vous sûr ?</p>
              <button className="btn-danger" onClick={onReset}>Oui</button>
              <button className="btn-secondary" onClick={() => setConfirmReset(false)}>Non</button>
            </div>
        }
      </div>
    </div>
  );
}

// ─── Main App ──────────────────────────────────────────────────────────────────
function App() {
  const [state, setState] = useState(DEFAULT_STATE);
  const [ready, setReady] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [flash, setFlash]               = useState(null);
  const [toast, setToast]               = useState(null);
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Check for existing authentication on app load
  useEffect(() => {
    const token = localStorage.getItem('spiritToken');
    const user = localStorage.getItem('spiritUser');
    
    if (token && user) {
      try {
        const userData = JSON.parse(user);
        setAuthToken(token);
        setCurrentUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        // Invalid stored data, clear it
        localStorage.removeItem('spiritToken');
        localStorage.removeItem('spiritUser');
      }
    }
    setAuthLoading(false);
  }, []);

  // Load state from server when authenticated
  useEffect(() => {
    if (!isAuthenticated || !authToken) return;
    
    fetch('https://server.bibleforce.com/api/state', {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    })
      .then(r => r.ok ? r.json() : null)
      .then(saved => {
        setState(saved || DEFAULT_STATE);
        setReady(true);
      })
      .catch(() => setReady(true));
  }, [isAuthenticated, authToken]);

  // Persist to server on every state change (only after initial load and when authenticated)
  useEffect(() => {
    if (!ready || !isAuthenticated || !authToken) return;
    fetch('https://server.bibleforce.com/api/state', {
      method:  'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body:    JSON.stringify(state),
    }).catch(() => {});
  }, [state, ready, isAuthenticated, authToken]);

  // Authentication handlers
  const handleAuthSuccess = (token, user) => {
    setAuthToken(token);
    setCurrentUser(user);
    setIsAuthenticated(true);
    setReady(false); // Will trigger data reload
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthToken(null);
    setCurrentUser(null);
    setReady(false);
    setState(DEFAULT_STATE);
  };

  const showToast = (text, type) => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 2500);
  };

  const handleActivity = (activity) => {
    if (state.todayActivities.includes(activity.id)) {
      showToast('Déjà accompli aujourd\'hui !', 'warning');
      return;
    }
    const now  = new Date();
    const today = now.toDateString();
    const time  = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    const displayDate = toDisplayDate(now);
    setFlash(activity.id);
    setTimeout(() => setFlash(null), 600);
    showToast(`+${activity.bonus}% — ${activity.label}`, 'success');
    setState(prev => ({
      ...prev,
      level:          Math.min(100, prev.level + activity.bonus),
      todayActivities: [...prev.todayActivities, activity.id],
      log: [
        { date: today, displayDate, time, icon: activity.icon, text: activity.label, delta: activity.bonus, type: 'activity' },
        ...prev.log.slice(0, 49),
      ],
    }));
  };

  const handleSin = () => {
    const now  = new Date();
    const today = now.toDateString();
    const time  = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    const displayDate = toDisplayDate(now);
    showToast('−5% — Péché confessé…', 'warning');
    setState(prev => ({
      ...prev,
      level: Math.max(0, prev.level - 5),
      log: [
        { date: today, displayDate, time, icon: '😔', text: 'Péché', delta: -5, type: 'decay' },
        ...prev.log.slice(0, 49),
      ],
    }));
  };

  const handleSaveSettings = (name, decay, period) => {
    setState(prev => ({ ...prev, avatarName: name, decayRate: decay, decayPeriod: period }));
    setShowSettings(false);
  };

  const handleReset = () => {
    setState({ ...DEFAULT_STATE, lastUpdated: new Date().toDateString() });
    setShowSettings(false);
  };

  const info   = getLevelInfo(state.level);
  const pColor = plumbobColor(state.level);
  const lvl    = Math.round(state.level);
  const isLow  = state.level < 20;

  // Show loading during initial auth check
  if (authLoading) {
    return (
      <div className="app app-loading">
        <div className="loading-spinner" />
        <p className="loading-text">Vérification de l'authentification…</p>
      </div>
    );
  }

  // Show auth form if not authenticated
  if (!isAuthenticated) {
    return <AuthForm onAuthSuccess={handleAuthSuccess} />;
  }

  // Show loading for authenticated users while loading their data
  if (!ready) {
    return (
      <div className="app app-loading">
        <div className="loading-spinner" />
        <p className="loading-text">Chargement de vos données spirituelles…</p>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Toast */}
      {toast && <div className={`toast toast-${toast.type}`}>{toast.text}</div>}

      {/* Header */}
      <header className="app-header">
        <span className="header-cross">✝</span>
        <div className="header-title-group">
          <h1 className="app-title">Saint-Esprit Avatar</h1>
          <p className="app-copyright">© 2026 BibleForce</p>
        </div>
        <UserMenu 
          user={currentUser} 
          onLogout={handleLogout}
          onShowSettings={() => setShowSettings(true)}
        />
      </header>

      <main className="app-main">

        {/* ── Avatar + Activities Frame ─────────────── */}
        <section className={`avatar-card ${isLow ? 'avatar-card--danger' : ''}`}>

          {/* Top row: avatar left | activities right */}
          <div className="avatar-card-top">

            {/* Left — avatar column */}
            <div className="avatar-col">
              <div className="plumbob-wrap">
                <div className="plumbob" style={{ '--pc': pColor }}>
                  <div className="plumbob-gem" />
                </div>
              </div>
              <AvatarSVG />
              <h2 className="avatar-name">{state.avatarName}</h2>
            </div>

            {/* Right — activities column */}
            <div className="acts-col">
              <h3 className="section-title">⚡ Activités Spirituelles</h3>
              <div className="activities-grid">
                {ACTIVITIES.map(act => {
                  const done = state.todayActivities.includes(act.id);
                  return (
                    <button
                      key={act.id}
                      className={`activity-card ${done ? 'done' : ''} ${flash === act.id ? 'flashing' : ''}`}
                      onClick={() => handleActivity(act)}
                    >
                      <span className="act-icon">{act.icon}</span>
                      <span className="act-label">{act.label}</span>
                      <span className="act-bonus">{done ? '✓ Fait' : `+${act.bonus}%`}</span>
                    </button>
                  );
                })}
                <button className="sin-btn" onClick={handleSin}>
                  😔 J&apos;ai péché <span className="sin-delta">–5%</span>
                </button>
              </div>
            </div>
          </div>

          {/* Bottom — spirit meter full width */}
          <div className="spirit-meter">
            <div className="spirit-meter-top">
              <span>🕊️ Niveau du Saint-Esprit</span>
              <span className="spirit-pct">{lvl}%</span>
            </div>
            <div className="spirit-track">
              <div
                className="spirit-fill"
                style={{ width: `${state.level}%`, '--fc': info.color }}
              />
            </div>
            <p className="spirit-status" style={{ color: info.color }}>{info.label}</p>
          </div>

          <div className="decay-badge">
            📉 Déclin : <strong>–{state.decayRate}% / {state.decayPeriod === 'daily' ? 'jour' : state.decayPeriod === 'hourly' ? 'heure' : 'minute'}</strong>
          </div>
        </section>

        {/* ── Journal ──────────────────────────────── */}
        {state.log.length > 0 && (
          <section className="log-section">
            <h3 className="section-title">📋 Journal Spirituel</h3>
            <div className="log-list">
              {state.log.slice(0, 10).map((entry, i) => (
                <div key={i} className={`log-entry log-${entry.type}`}>
                  <span className="log-icon">{entry.icon}</span>
                  <span className="log-text">
                    <span className="log-time">
                      {entry.displayDate || entry.date}{entry.time ? ` ${entry.time}` : ''} —
                    </span>
                    {' '}{entry.text}
                  </span>
                  <span className={`log-delta ${entry.delta > 0 ? 'pos' : 'neg'}`}>
                    {entry.delta > 0 ? '+' : ''}{Math.round(entry.delta)}%
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

      </main>

      {showSettings && (
        <SettingsModal
          avatarName={state.avatarName}
          decayRate={state.decayRate}
          decayPeriod={state.decayPeriod}
          onSave={handleSaveSettings}
          onReset={handleReset}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}

export default App;
